/**
 * AuthContext — who is signed in, and whether they may expand sending.
 *
 * The trust model is the one Console, Designer and Explorer already use. The
 * Hanko session cookie is scoped to .samsurveyor.com. It rides on a credentialed
 * request to sam-dbam's /dbam/v1/me, sam-dbam verifies it against Hanko's key
 * set, and answers with the CloudSAM profile.
 *
 * This surface adds one thing the others do not need: whether this person is an
 * administrator of the account. The Channels screen hides buttons a person may
 * not use, and the answer here decides which.
 *
 * That answer is a courtesy and never a control. Every one of those actions is
 * refused again by sam-dbam, which works the same question out from the verified
 * session. Anyone calling the API directly never sees this screen at all, so a
 * permission enforced only here would be no permission.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { END_REASON_SIGNOUT } from '@cloudsam/ui';
import hanko from '../hanko-init';
import { setUnauthorizedHandler } from '../utils/channelsApi';
import { idleClock, draftStore, setDraftUserId } from '../idle-session';
import { getDbamBase } from '../dbamBase';
import { REQUIRE_AUTH } from '../config';
import { getSessionToken } from '../utils/session';

const AuthContext = createContext(null);

// The address itself now lives in src/dbamBase.js, because the idle clock
// needs it and this file imports the idle clock — importing it back would be a
// cycle. Imported for use below and re-exported, because several modules and
// their tests import the name from this file.
export { getDbamBase };

/**
 * isAdminProfile mirrors the rule sam-dbam applies in IsOrgAdmin: a platform
 * superuser, or somebody whose role in the account is owner or admin.
 *
 * It is kept as a named function rather than written inline so the two copies of
 * the rule can be compared. If they ever disagree, the screen offers a button
 * the service then refuses, which is a confusing way to be told no.
 */
export function isAdminProfile(profile) {
    if (!profile) return false;
    if (profile.role === 'superuser') return true;
    return profile.org_role === 'owner' || profile.org_role === 'admin';
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // True once the idle clock has been started for the session this page
    // load is holding. Reset when a check finds nobody signed in, so a
    // sign-in later in the same page load starts the clock again.
    const sessionStartedRef = useRef(false);

    const checkAuth = useCallback(async () => {
        // With sign-in switched off and no token pasted there is nobody to look
        // up, and asking would fail for a reason that has nothing to do with
        // this screen. A pasted token is a real session and is looked up.
        const bearer = getSessionToken();
        if (!REQUIRE_AUTH && !bearer) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            setError(null);
            // Do not gate this on the Hanko SDK's own session check. That check
            // reads storage belonging to this origin alone, so on a subdomain
            // that has never signed anybody in it answers no while the cookie is
            // sitting there working. Calling /me and reading the answer is the
            // only reliable test.
            const response = await fetch(`${getDbamBase()}/dbam/v1/me`, {
                credentials: 'include',
                headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
            });

            if (response.ok) {
                const body = await response.json();
                const profile = body?.data || body;
                setUser(profile);
                // Start the idle clock for this sign-in.
                //
                // This hangs on the session check, not on the Hanko SDK's
                // onSessionCreated event. Both sign-in screens drive the Flow API
                // and call back from its own `success` handler, so that event is not
                // a reliable signal. Relying on it left the end-of-session cookie in
                // place, and every tab opened afterwards locked itself against a
                // session that had just been proved good.
                //
                // It fires on the change from "no session" to "session", so a fresh
                // page load does it once and a repeat check does not touch the clock.
                if (!sessionStartedRef.current) {
                    sessionStartedRef.current = true;
                    idleClock.startSession();
                }
                // Panels that write recovery drafts key them by user id, and
                // they sit too far below this context to be handed a prop.
                setDraftUserId(profile?.user_id || '');
                // Two people can share a machine. A recovery draft left by
                // somebody else must never be offered to this person.
                try {
                    draftStore.clearDraftsForOtherUsers(profile?.user_id || '');
                } catch (err) {
                    console.warn('[channels] could not clear other accounts drafts');
                }
            } else {
                // 401 means no session. 404 means signed in to Hanko but with no
                // CloudSAM profile yet. Neither can use this screen, and both
                // are answered by the sign-in gate.
                sessionStartedRef.current = false;
                setUser(null);
            }
        } catch (err) {
            setError(err);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
        if (!REQUIRE_AUTH) return undefined;

        // A session can die while the tab stays open. A 401 from any call is an
        // earlier and more reliable signal than Hanko's own event, so it counts
        // as the session ending.
        setUnauthorizedHandler(() => setUser(null));

        hanko.onSessionExpired(() => setUser(null));
        hanko.onUserLoggedOut(() => setUser(null));
        // Clear the "this session has ended" cookie and restart the idle clock.
        // Without this, somebody who signs back in is locked out again on the
        // next tick by the cookie left from the sign-out before.
        hanko.onSessionCreated(() => { idleClock.startSession(); checkAuth(); });

        return () => setUnauthorizedHandler(null);
    }, [checkAuth]);

    const logout = useCallback(async () => {
        if (!REQUIRE_AUTH) return;
        // Tell the other tabs first, before any network call, so Console,
        // Designer and Explorer lock straight away rather than waiting on
        // Hanko. They cannot be closed from here; they watch a shared cookie.
        idleClock.endSession(END_REASON_SIGNOUT);
        // Tell CloudSAM first, while the session still works. Hanko does not
        // report a sign-out, so without this the stored expiry stays in the
        // future and this person keeps appearing as signed in. It must never
        // block the sign-out itself.
        try {
            await fetch(`${getDbamBase()}/dbam/v1/me/signout`, { method: 'POST', credentials: 'include' });
        } catch (err) {
            console.warn('[channels] sign-out could not be recorded');
        }
        try {
            await hanko.user.logout();
        } catch (err) {
            console.error('[channels] logout error:', err);
        } finally {
            setUser(null);
        }
    }, []);

    const refreshUser = useCallback(async () => { await checkAuth(); }, [checkAuth]);

    // With sign-in off, every control is reachable. The permission rule is a
    // courtesy on this screen and is decided again by the service, which is not
    // being called at all here. Greying out half the screen would only hide the
    // parts somebody is trying to look at.
    const signedOutPreview = !REQUIRE_AUTH;

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            error,
            logout,
            refreshUser,
            signedOutPreview,
            isAuthenticated: signedOutPreview ? false : !!user?.user_id,
            isAdmin: signedOutPreview ? true : isAdminProfile(user),
            roleName: signedOutPreview
                ? 'not signed in'
                : (isAdminProfile(user) ? 'Platform Admin' : 'Survey Author'),
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
